import {memo, useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {
  PaddingBottom,
  TextHeading1,
  TextHeading5,
  TextHeading6,
  TextHeading7,
  PrimaryButton,
} from '../../../../../../../components';
import {
  useAppDispatch,
  useAppSelector,
  useI18n,
} from '../../../../../../../hooks';
import {GetStartedActions} from '../../../../../../../store/get-started/GetStartedActions';
import {GetStartedSelector} from '../../../../../../../store/get-started/GetStartedSelector';
import {GlobalActions} from '../../../../../../../store/global/GlobalActions';
import {
  GetStartedWizardStepContainer,
  GetStartedWizardStepContainerProps,
} from '../../step-container/GetStartedWizardStepContainer';
import {RootRoute} from '../../../../../../../routes/RootRoute';
import {useAuth} from '../../../../../../../store/AuthProvider';

const SummaryCard = styled.div`
  background: ${Palette.white};
  border-radius: 12px;
  padding: 24px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SummaryLabel = styled.span`
  color: ${Palette.almostBlack500};
  font-size: 14px;
`;

const SummaryValue = styled.span`
  color: ${Palette.almostBlack};
  font-size: 14px;
  font-weight: 500;
`;

export const Step3 = memo(
  ({previousStep}: GetStartedWizardStepContainerProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {refreshAdminStatus, setIsAdminView} = useAuth();
    const t = useI18n();
    const companyName = useAppSelector(
      GetStartedSelector.getWrittenCompanyName,
    );
    const fullName = useAppSelector(GetStartedSelector.getUserFullName);
    const invitedEmployees = useAppSelector(
      GetStartedSelector.getInvitedEmployees,
    );
    const [isCreating, setIsCreating] = useState(false);

    const handleCreateCompany = useCallback(async () => {
      try {
        setIsCreating(true);
        dispatch(GlobalActions.setIsBusy(true));

        await dispatch(GetStartedActions.finalizeCompanyCreation()).unwrap();

        // Refresh admin status to update isAdmin state in AuthProvider
        await refreshAdminStatus();

        // Explicitly set admin view to true since user is now an admin
        setIsAdminView(true);

        // Small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 500));

        // Navigate to admin dashboard with flag for tutorial
        navigate(RootRoute.AuthAdminHome, {
          replace: true,
          state: {showWelcomeTutorial: true},
        });
      } catch (error) {
        console.error('Error creating company:', error);
        alert('Failed to create company. Please try again.');
      } finally {
        setIsCreating(false);
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, navigate, refreshAdminStatus, setIsAdminView]);

    return (
      <GetStartedWizardStepContainer previousStep={previousStep}>
        <div style={{width: '100%', maxWidth: '450px', margin: '0 auto'}}>
          <TextHeading1 center text={t.scenes.getStarted.readyToLaunch} />
          <PaddingBottom amount={12} />
          <TextHeading6
            text={t.scenes.getStarted.readyToLaunchDescription}
            color={Palette.almostBlack500}
            center
          />
          <PaddingBottom amount={24} />

          <SummaryCard>
            <SummaryRow>
              <SummaryItem>
                <SummaryLabel>{t.scenes.getStarted.admin}:</SummaryLabel>
                <SummaryValue>{fullName}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>{t.common.company}:</SummaryLabel>
                <SummaryValue>{companyName}</SummaryValue>
              </SummaryItem>
              {invitedEmployees.length > 0 && (
                <SummaryItem>
                  <SummaryLabel>{t.common.invited}:</SummaryLabel>
                  <SummaryValue>
                    {invitedEmployees.length}{' '}
                    {invitedEmployees.length === 1
                      ? t.scenes.getStarted.employee
                      : t.scenes.getStarted.employees}
                  </SummaryValue>
                </SummaryItem>
              )}
            </SummaryRow>
          </SummaryCard>

          <PaddingBottom amount={24} />

          <ButtonContainer>
            <PrimaryButton
              label={
                isCreating
                  ? t.scenes.getStarted.creatingCompany
                  : t.scenes.getStarted.createCompany
              }
              onClick={handleCreateCompany}
              isDisabled={isCreating}
              fullWidth
            />
          </ButtonContainer>
        </div>
      </GetStartedWizardStepContainer>
    );
  },
);
